import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import html2canvas from 'html2canvas';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ShareableWinnerScreen = ({ isOpen, onClose, duel, userId, userStats }) => {
  const [generating, setGenerating] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const screenRef = useRef(null);

  if (!isOpen || !duel) return null;

  const isCreator = duel?.creator_id === userId;
  const isWinner = duel?.winner_id === userId;
  const userProfile = isCreator ? duel?.creator : duel?.opponent;
  const opponentProfile = isCreator ? duel?.opponent : duel?.creator;
  const userProgress = isCreator ? duel?.creator_progress : duel?.opponent_progress;
  const opponentProgress = isCreator ? duel?.opponent_progress : duel?.creator_progress;

  const handleDownloadImage = async () => {
    setGenerating(true);
    try {
      const element = screenRef?.current;
      const canvas = await html2canvas(element, {
        backgroundColor: '#1a1a1a',
        scale: 2,
        logging: false
      });
      
      const link = document.createElement('a');
      link.download = `vitaltrack-duel-${duel?.id}.png`;
      link.href = canvas?.toDataURL('image/png');
      link?.click();
    } catch (error) {
      console.error('Error generating image:', error);
    } finally {
      setGenerating(false);
    }
  };

  const handleShareToInstagram = async () => {
    setGenerating(true);
    try {
      const element = screenRef?.current;
      const canvas = await html2canvas(element, {
        backgroundColor: '#1a1a1a',
        scale: 2,
        logging: false
      });
      
      canvas?.toBlob(async (blob) => {
        const file = new File([blob], 'vitaltrack-duel.png', { type: 'image/png' });
        
        if (navigator.share && navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: '¡Gané mi duelo en VitalTrack!',
            text: `¡Acabo de ganar un duelo de ${duel?.title}! 🏆`
          });
        } else {
          // Fallback: download the image
          handleDownloadImage();
        }
      });
    } catch (error) {
      console.error('Error sharing:', error);
    } finally {
      setGenerating(false);
    }
  };

  const handleCopyLink = () => {
    const link = `${window.location?.origin}/social-hub?duel=${duel?.id}`;
    navigator.clipboard?.writeText(link);
    setShareUrl(link);
    setTimeout(() => setShareUrl(null), 3000);
  };

  const modalContent = (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      />
      
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="relative w-full max-w-2xl bg-card rounded-xl shadow-wellness-lg elevation-3 overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border/40">
          <h2 className="text-xl font-bold text-foreground">¡Comparte tu Victoria!</h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        <div className="p-6 space-y-6">
          {/* Shareable Card */}
          <div 
            ref={screenRef}
            className="relative bg-gradient-to-br from-success/20 via-card to-primary/20 rounded-2xl p-8 border-2 border-success/30 overflow-hidden"
          >
            {/* Confetti Background */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              {[...Array(15)]?.map((_, i) => (
                <div
                  key={i}
                  className="absolute w-2 h-2 bg-primary rounded-full opacity-30"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`
                  }}
                />
              ))}
            </div>

            {/* Content */}
            <div className="relative space-y-6">
              {/* Trophy Icon */}
              <div className="flex justify-center">
                <div className="w-20 h-20 rounded-full bg-success/20 flex items-center justify-center">
                  <Icon name="Trophy" size={48} className="text-success" />
                </div>
              </div>

              {/* Winner Announcement */}
              <div className="text-center space-y-2">
                <h3 className="text-3xl font-bold text-foreground">¡Victoria!</h3>
                <p className="text-lg text-muted-foreground">
                  {userProfile?.full_name} ganó el duelo
                </p>
              </div>

              {/* Challenge Info */}
              <div className="bg-card/50 rounded-xl p-4 border border-border/40">
                <h4 className="text-lg font-semibold text-foreground mb-2">{duel?.title}</h4>
                <p className="text-sm text-muted-foreground mb-3">{duel?.description}</p>
                <div className="flex items-center justify-center space-x-8">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-success">{userProgress}</p>
                    <p className="text-xs text-muted-foreground">Ganador</p>
                  </div>
                  <div className="text-4xl font-bold text-muted-foreground">VS</div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-muted-foreground">{opponentProgress}</p>
                    <p className="text-xs text-muted-foreground">{opponentProfile?.full_name}</p>
                  </div>
                </div>
              </div>

              {/* Stats Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-3 bg-card/50 rounded-lg border border-border/40">
                  <Icon name="Target" size={24} className="text-primary mx-auto mb-1" />
                  <p className="text-lg font-bold text-foreground">{duel?.target_value}</p>
                  <p className="text-xs text-muted-foreground">Meta</p>
                </div>
                <div className="text-center p-3 bg-card/50 rounded-lg border border-border/40">
                  <Icon name="Calendar" size={24} className="text-accent mx-auto mb-1" />
                  <p className="text-lg font-bold text-foreground">{duel?.duration_days}</p>
                  <p className="text-xs text-muted-foreground">Días</p>
                </div>
                <div className="text-center p-3 bg-card/50 rounded-lg border border-border/40">
                  <Icon name="Award" size={24} className="text-warning mx-auto mb-1" />
                  <p className="text-lg font-bold text-foreground">{userStats?.duels_won || 0}</p>
                  <p className="text-xs text-muted-foreground">Victorias</p>
                </div>
              </div>

              {/* Branding */}
              <div className="text-center pt-4 border-t border-border/40">
                <p className="text-sm font-semibold text-primary">VitalTrack</p>
                <p className="text-xs text-muted-foreground">Wellness Gamification Platform</p>
              </div>
            </div>
          </div>

          {/* Share Actions */}
          <div className="space-y-3">
            <Button
              onClick={handleShareToInstagram}
              loading={generating}
              disabled={generating}
              fullWidth
              size="lg"
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
            >
              <Icon name="Instagram" size={20} className="mr-2" />
              Compartir en Instagram Stories
            </Button>

            <div className="grid grid-cols-2 gap-3">
              <Button
                variant="outline"
                onClick={handleDownloadImage}
                loading={generating}
                disabled={generating}
              >
                <Icon name="Download" size={16} className="mr-2" />
                Descargar Imagen
              </Button>
              <Button
                variant="outline"
                onClick={handleCopyLink}
              >
                <Icon name={shareUrl ? "Check" : "Link"} size={16} className="mr-2" />
                {shareUrl ? '¡Copiado!' : 'Copiar Link'}
              </Button>
            </div>
          </div>

          {/* Viral Copy */}
          <div className="text-center p-4 bg-primary/5 rounded-xl border border-primary/20">
            <p className="text-sm text-muted-foreground">
              💪 Comparte tu victoria y desafía a tus amigos a superarte
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default ShareableWinnerScreen;